import type {
  IChatHistoryItem,
  ISingleChatBubble,
} from "@/types/(my-space)/ai-assistant.interface";

import { isHttpUrl } from "@/utils/file.utils";

import type { IMayaMessage, MayaMessageRole } from "../types";

/** Turns (question + answer pairs) fetched per page of a saved chat. */
export const HISTORY_PAGE_SIZE = 20;

/** Stable id for a stored message — the same turn always maps to the same
 *  id, so overlapping pages can be de-duplicated. */
export const storedMessageId = (id: number | string) => `srv-${id}`;

const fileName = (url: string) => {
  const path = url.split("?")[0];
  const raw = path.substring(path.lastIndexOf("/") + 1);
  // A stray "%" makes decodeURIComponent throw — show the raw name rather
  // than failing the whole chat over one filename.
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
};

const toMessage = (
  bubble: ISingleChatBubble,
  role: MayaMessageRole,
): IMayaMessage => {
  // Only http(s) links reach `href` / `src` (see isHttpUrl).
  const sent = (bubble.attachmentUrls ?? [])
    .filter(isHttpUrl)
    .map((url) => ({ name: fileName(url), url }));
  const generated = (bubble.generatedAttachments ?? []).filter((a) =>
    isHttpUrl(a.url),
  );
  return {
    id: storedMessageId(bubble.id),
    serverId: bubble.id,
    role,
    text: bubble.content ?? "",
    ts: Date.parse(bubble.createdAt) || 0,
    status: "sent",
    ...(sent.length ? { attachments: sent } : {}),
    ...(generated.length ? { generatedAttachments: generated } : {}),
    ...(bubble.attachmentErrors?.length
      ? { attachmentErrors: bubble.attachmentErrors }
      : {}),
  };
};

/** A reply worth showing: text, or files (or the reason files failed). */
const hasReply = (answer: ISingleChatBubble | null | undefined) =>
  !!answer &&
  (!!answer.content ||
    !!answer.generatedAttachments?.length ||
    !!answer.attachmentErrors?.length);

/**
 * A page of saved history (newest pair first, as `GET /history` returns it)
 * as sidebar messages, oldest first, with the files on either side of each
 * turn. A turn whose reply never arrived comes back with a null answer — it
 * shows as the question alone.
 */
export const historyToMessages = (
  history: IChatHistoryItem[],
): IMayaMessage[] =>
  [...history].reverse().flatMap(({ question, answer }) => {
    const out: IMayaMessage[] = [];
    if (question) out.push(toMessage(question, "user"));
    if (answer && hasReply(answer)) out.push(toMessage(answer, "bot"));
    return out;
  });

/**
 * Older messages placed before the ones already shown, skipping any already
 * there: a page boundary shifts when turns are added after the first load,
 * so consecutive pages can overlap.
 */
export const prependMessages = (
  current: IMayaMessage[],
  older: IMayaMessage[],
): IMayaMessage[] => {
  // Turns sent in this session keep their client ids but learn their stored
  // id as they're saved (`serverId`), so they're recognised on older pages.
  const seen = new Set(
    current.map((m) => (m.serverId ? storedMessageId(m.serverId) : m.id)),
  );
  const fresh = older.filter((m) => !seen.has(m.id));
  // The greeting (an i18n key, never stored) stays on top.
  const [first, ...rest] = current;
  return first?.i18nKey ? [first, ...fresh, ...rest] : [...fresh, ...current];
};
